/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { StatusCodes } from 'http-status-codes';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const newrelic = require('newrelic'); // must be required first
import { NextPage } from 'next';
import { Store } from 'redux';
import { LogicMiddleware } from 'redux-logic';

import { listPopular, loadRelated } from 'modules/job-types/duck';

import { get as getJobTypeDetail } from 'modules/job-types/service';

import FeatureFlagsService from 'src/services/feature-flags';

const featureFlagsService = FeatureFlagsService.getInstance();

import type { VerticalPageJobType } from 'modules/job-types/types';
import {
  citySelector,
  getCity,
  listCities,
  listCityGroups,
  listPrefectures,
  getPrefecture,
  prefectureSelector,
} from 'modules/locations/duck';

import LandingPage from 'modules/marketing-pages/components/LandingPage/LandingPage';
import type { LandingPageProps } from 'modules/marketing-pages/components/LandingPage/LandingPage';
import {
  getNewestReviewsByJobType,
  getNewestReviewsByJobTypeAndPrefecture,
  getPopularPricingItems,
  listPros,
  listProsGroup,
  listProBlogPostsByJobType,
  getCaseStudies,
  getPopularRequests,
  proCountSelector,
  initializeFilters,
} from 'modules/marketing-pages/duck';
import { getProCount, getInternalBlogPostsByJobTypeId } from 'modules/marketing-pages/service';
import {
  caseStudiesPageSizeSelector,
  caseStudiesTotalCountSelector,
  proCountGroupSelector,
} from 'modules/marketing-pages/duck/selectors';
import {
  setNewRequestModalProperties,
  setNewRequestModalContext,
} from 'modules/requests/duck/actions';
import type { RootState } from 'store/types';
import { setCachedStates } from 'store/actions';
import {
  checkAndRedirectIfNeeded,
  CATEGORY_WITH_ONLY_ONE_SUB_CATEGORY,
} from 'utils/marketing-pages';
import { isDefined } from 'utils/type-guards';
import { wrapper } from 'store/index';
import { setupPageAndLoadTranslations } from 'utils/page';
import { removeUndefinedForNextJsSerializing } from 'utils/data-transform/next-undefined';
import { getIsHomeRequestFormEntry } from 'utils/job-type';
import { transformFilterQueryStringToFilterState } from 'modules/marketing-pages/utils';
import { MarketingPageContext } from 'src/context/marketing-page';
import { GoogleOptimizeContext } from 'src/context/google-optimize';
import type { BlogPost } from 'modules/marketing-pages/marketing-page.types';
import CityGroupingService from 'src/services/city-grouping';

import { getSSRCache, setSSRCache } from 'src/ssr-cache';
import { generateUrlWithoutUnwantedParameters } from 'src/utils/url';
import { useGoogleOptimize } from 'src/utils/google-optimize-manager';
import {
  GOOGLE_OPTIMIZE_INTRO_STEP_DESC_TEST_ID,
  GOOGLE_OPTIMIZE_FILTER_AND_DISPLAY_THE_NUMBER_OF_PROFESSIONALS_WITHIN_A_REQUEST_FORM_TEST_ID,
} from 'constants/google-optimize';
import { NUM_NEARBY_CITIES_PROS_PER_PAGE } from 'constants/vertical-lp';
import { useEffect } from 'react';
import { getAndParseItem } from 'utils/localStorage';
import Cookies from '@z-components/utils/cookies';

const DURATION = 100;
const cityGroupingService = new CityGroupingService();

const JOB_TYPE_PAGE = 'jobType';
const PREFECTURE_PAGE = 'prefecture';
const CITY_PAGE = 'city';
const GROUP_PAGE = 'group';
const GROUP_CITY_PAGE = 'groupCity';

const GROUP_CITY_NUM_CITY_PROS_PER_PAGE = 6;
const GROUP_CITY_NUM_GROUP_PROS_PER_PAGE = 6;

interface Props {
  cityGroup?: string;
  cityGroupName?: string;
  cityId?: string;
  day?: string;
  featureFlags: { isReviewsQAEnabled: boolean };
  groupLink: string;
  initialPageNumber: number;
  initialPageNumberForCaseStudy: number;
  internalBlogPosts: Array<BlogPost>;
  jobType: VerticalPageJobType;
  jobTypeId: string;
  numOfCityPros: number;
  numOfGroupPros: number;
  online?: LandingPageProps['online'];
  pageTranslationId: 'areaPrefectureCityJobTypePage' | 'areaPrefectureVertical' | 'verticalLP';
  pageType: LandingPageProps['pageType'];
  prefectureId?: string;
  shouldOpenRequestModal?: LandingPageProps['shouldOpenRequestModal'];
  time?: string;
  totalPros: number;
}

const VerticalPage: NextPage<Props> = ({
  cityGroup,
  cityGroupName,
  numOfCityPros,
  numOfGroupPros,
  totalPros,
  featureFlags,
  groupLink,
  cityId,
  initialPageNumber,
  jobType,
  jobTypeId,
  online,
  pageTranslationId,
  pageType,
  prefectureId,
  shouldOpenRequestModal,
  initialPageNumberForCaseStudy,
  internalBlogPosts,
}) => {
  const introStepVariant = useGoogleOptimize(GOOGLE_OPTIMIZE_INTRO_STEP_DESC_TEST_ID, DURATION);
  const isHomeRequestFormEntry = getIsHomeRequestFormEntry(jobType);
  const testVariant = useGoogleOptimize(
    GOOGLE_OPTIMIZE_FILTER_AND_DISPLAY_THE_NUMBER_OF_PROFESSIONALS_WITHIN_A_REQUEST_FORM_TEST_ID,
    DURATION
  );

  const currentUid = getAndParseItem<string>('ngStorage-currentUid');
  Cookies.setCookie(
    {
      key: 'z-user-not-login',
      value: currentUid === null ? 'true' : 'false',
    },
    { url: process.env.DOMAIN ?? '' }
  );

  useEffect(() => {
    // Perform check against the url. If the vertical url is broken, Change it to the proper url
    const browserUrl = document.location.href;
    if (browserUrl.includes('/vertical')) {
      const urlObject = new URL(browserUrl);
      const urlJobTypeId = urlObject.searchParams.get('jobTypeId');
      if (urlJobTypeId) {
        // Cleanup the history
        window.history.replaceState({}, '', `/${urlJobTypeId}`);
        // REplace the href with the path to the job-type-id
        window.location.href = `/${urlJobTypeId}`;
      }
    }
  }, []);

  return (
    <GoogleOptimizeContext.Provider
      value={{ variant: isHomeRequestFormEntry ? introStepVariant : '0' }}
    >
      <MarketingPageContext.Provider value={{ internalBlogPosts }}>
        <LandingPage
          cityGroup={cityGroup}
          cityGroupName={cityGroupName}
          featureFlags={featureFlags}
          groupLink={groupLink}
          cityId={cityId}
          initialPageNumber={initialPageNumber}
          initialPageNumberForCaseStudy={initialPageNumberForCaseStudy}
          jobType={jobType}
          jobTypeId={jobTypeId}
          online={online}
          pageTranslationId={pageTranslationId}
          pageType={pageType}
          prefectureId={prefectureId}
          shouldOpenRequestModal={shouldOpenRequestModal}
          numOfCityPros={numOfCityPros}
          numOfGroupPros={numOfGroupPros}
          totalPros={totalPros}
          testVariant={testVariant}
        />
      </MarketingPageContext.Provider>
    </GoogleOptimizeContext.Provider>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, defaultLocale, locale, resolvedUrl, query }) => {
      const { dispatch, logicMiddleware } = store as Store<RootState> & {
        logicMiddleware: LogicMiddleware;
      };

      if (resolvedUrl) {
        const reductedResolvedUrl = generateUrlWithoutUnwantedParameters(resolvedUrl);
        if (reductedResolvedUrl !== resolvedUrl) {
          // The permanent redirection here is done to prevent google search console from flagging page as duplicate
          return {
            redirect: {
              destination: reductedResolvedUrl,
              permanent: true,
            },
          };
        }
      }

      const cacheKeyProps = {
        locale,
        pageName: 'vertical',
        query,
        url: req.url ?? '',
      };
      const cachedProps = await getSSRCache(cacheKeyProps, 'props');
      const cachedStates = await getSSRCache(cacheKeyProps, 'states');

      if (cachedProps && cachedStates) {
        dispatch(setCachedStates(cachedStates as RootState));
        await logicMiddleware.whenComplete();
        return { props: cachedProps };
      }

      const {
        category1 = CATEGORY_WITH_ONLY_ONE_SUB_CATEGORY,
        category2,
        category3,
        day,
        groupKeyOrCityKey,
        groupedCityKey,
        jobTypeId,
        matchingCriteria = '',
        online,
        page,
        priceRange,
        caseStudyPage = '1',
        prefectureId: prefectureKey,
        shouldOpenRequestModal,
        time,
        language = '',
        ...restQueryString
      } = query as {
        caseStudyPage?: string;
        category1: string;
        category2: string;
        category3?: string;
        day?: string;
        groupKeyOrCityKey?: string;
        groupedCityKey?: string;
        jobTypeId: string;
        language?: string;
        matchingCriteria?: string;
        online?: string;
        page?: string;
        prefectureId?: string;
        priceRange?: string;
        shouldOpenRequestModal?: string;
        time?: string;
      };

      const resolvedLocation = await cityGroupingService.resolveLocation(
        jobTypeId,
        prefectureKey,
        groupKeyOrCityKey,
        groupedCityKey
      );

      const { cityKey, groupKey = '', notFound } = resolvedLocation;

      if (notFound) {
        return {
          notFound: true,
        };
      }

      const redirectionResult = await checkAndRedirectIfNeeded<Props>({
        categories: [category1, category2, category3].filter(isDefined),
        dispatch,
        jobTypeId,
        logicMiddleware,
        prefectureKey,
        resolvedLocation,
        store,
        url: resolvedUrl,
      });

      if (redirectionResult) {
        return redirectionResult;
      }

      let pageType: Props['pageType'];
      let pageTranslationId: Props['pageTranslationId'];
      if (groupKey && cityKey) {
        pageType = GROUP_CITY_PAGE;
        pageTranslationId = 'areaPrefectureCityJobTypePage';
      } else if (groupKey && !cityKey) {
        pageType = GROUP_PAGE;
        pageTranslationId = 'areaPrefectureVertical';
      } else if (cityKey) {
        pageType = CITY_PAGE;
        pageTranslationId = 'areaPrefectureCityJobTypePage';
      } else if (prefectureKey) {
        pageType = PREFECTURE_PAGE;
        pageTranslationId = 'areaPrefectureVertical';
      } else {
        pageType = JOB_TYPE_PAGE;
        pageTranslationId = 'verticalLP';
      }

      const { currentLocale, i18nData } = await setupPageAndLoadTranslations({
        defaultLocale,
        locale,
        namespaces: ['common', 'form', 'marketingPages', 'request', 'users', 'verticalPages'],
        remoteTranslations: ['AreaHowItWorksWidget', 'verticalLP', 'newRequest', pageTranslationId],
        // @ts-expect-error Extended store
        store,
      });

      const initialPageNumber = Number(page) || 1;
      const initialPageNumberForCaseStudy = Number(caseStudyPage) || 1;
      dispatch(getCity({ cityKey, prefectureKey }));
      dispatch(getPrefecture({ prefectureKey }));

      let jobType;
      try {
        jobType = (await getJobTypeDetail(jobTypeId, currentLocale, 'vertical-page')) as
          | VerticalPageJobType
          | undefined;
      } catch (error: any) {
        if (error.response?.status === StatusCodes.NOT_FOUND) {
          // invalid jobType provided
          return { notFound: true };
        }
        throw error;
      }

      if (jobType?.filterSettings) {
        const initialFilterState = transformFilterQueryStringToFilterState({
          day,
          matchingCriteria,
          priceRange,
          time,
        });
        dispatch(initializeFilters(initialFilterState));
      }
      const isGroupCity = groupKey && cityKey;
      const numOfCityOrNearbyProsPerPage = isGroupCity
        ? GROUP_CITY_NUM_CITY_PROS_PER_PAGE
        : NUM_NEARBY_CITIES_PROS_PER_PAGE;

      const numOfCityPros = numOfCityOrNearbyProsPerPage;
      const numOfGroupPros = GROUP_CITY_NUM_GROUP_PROS_PER_PAGE;

      const groupLink = isGroupCity
        ? [
            '',
            category1,
            category2,
            category3,
            jobTypeId,
            prefectureKey,
            resolvedLocation.groupKey,
          ].join('/')
        : '';

      dispatch(
        listPros({
          cityGroup: groupKey,
          cityKey,
          day,
          jobTypeId,
          language: currentLocale,
          matchingCriteria,
          page: initialPageNumber,
          prefectureKey,
          priceRange,
          size: numOfCityOrNearbyProsPerPage,
          skip: numOfCityOrNearbyProsPerPage * (initialPageNumber - 1),
          time,
        })
      );

      let proCount;
      let path;
      if (pageType === GROUP_CITY_PAGE) {
        await logicMiddleware.whenComplete();
        proCount = proCountSelector(store.getState());

        path = [category1, category2, category3, jobTypeId, prefectureKey, groupKey].filter(
          isDefined
        );
      } else {
        [{ count: proCount }] = await Promise.all([
          getProCount({ cityKey, jobTypeId, language: currentLocale, prefectureKey }).catch(() => ({
            count: 0,
          })),
          logicMiddleware.whenComplete(),
        ]);

        path = [category1, category2, category3, jobTypeId, prefectureKey, cityKey].filter(
          isDefined
        );
        path.pop();
      }

      if (proCount === 0) {
        const params = new URLSearchParams({ ...restQueryString }).toString();
        const redirectURL = `${language === 'en' ? '/en/' : '/'}${path.join('/')}${
          params ? `?${params}` : ''
        }`;
        return { redirect: { destination: redirectURL, permanent: false } };
      }

      if (isGroupCity) {
        const excludeCities = cityKey ? [cityKey] : [];
        dispatch(
          listProsGroup({
            cityGroup: groupKey,
            cityKey,
            day,
            excludeCities,
            jobTypeId,
            language: currentLocale,
            matchingCriteria,
            page: 1,
            prefectureKey,
            priceRange,
            size: GROUP_CITY_NUM_GROUP_PROS_PER_PAGE,
            skip: 0,
            time,
          })
        );
      }

      dispatch(
        getCaseStudies({
          cityKey,
          prefectureKey,
          jobTypeId,
          currentPage: initialPageNumberForCaseStudy,
        })
      );

      dispatch(
        getPopularRequests({
          cityKey,
          prefectureKey,
          jobTypeId,
        })
      );

      let cityGroupName = '';

      if (pageType === CITY_PAGE) {
        // @ts-expect-error citySelector is not yet typed
        const city = citySelector(store.getState(), { cityKey });
        if (!city || !city.key) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          newrelic.addCustomAttributes({ reason: 'city' });
          return { notFound: true };
        }
      } else if (pageType === PREFECTURE_PAGE) {
        // @ts-expect-error prefectureSelector is not yet typed
        const prefecture = prefectureSelector(store.getState(), { prefectureKey });
        if (!prefecture || !prefecture.key) {
          newrelic.addCustomAttributes({ reason: 'prefecture' });
          return { notFound: true };
        }
      }

      if (!jobType || !jobType.clientRequest) {
        newrelic.addCustomAttributes({ reason: 'jobType' });
        return { notFound: true };
      }

      if (pageType === GROUP_PAGE || pageType === GROUP_CITY_PAGE || pageType === CITY_PAGE) {
        cityGroupName = await cityGroupingService.getCityGroupNameByKey(
          groupKey,
          language as 'en' | 'ja'
        );
      }

      dispatch(setNewRequestModalProperties({ jobTypeId }));
      const internalBlogPostItems: Array<BlogPost> = await getInternalBlogPostsByJobTypeId({
        jobTypeId,
      });

      switch (pageType) {
        case JOB_TYPE_PAGE:
          {
            dispatch(loadRelated({ jobTypeId, requestableOnly: true }));
            dispatch(listPopular());
            dispatch(listPrefectures({ jobTypeId }));
            dispatch(listProBlogPostsByJobType(jobTypeId));
            dispatch(getNewestReviewsByJobType(jobTypeId));
          }
          break;
        case PREFECTURE_PAGE:
          dispatch(setNewRequestModalContext({ prefecture: { key: prefectureKey } }));
          dispatch(loadRelated({ jobTypeId, prefectureId: prefectureKey, requestableOnly: true }));
          dispatch(listPopular({ prefectureKey }));
          if (resolvedLocation.isCityGroupingEnabled) {
            dispatch(listCityGroups({ prefectureKey }));
          } else {
            dispatch(listCities({ cityKey, jobTypeId, prefectureKey }));
          }
          dispatch(getNewestReviewsByJobTypeAndPrefecture(jobTypeId, prefectureKey ?? ''));
          break;
        case GROUP_PAGE:
          dispatch(setNewRequestModalContext({ prefecture: { key: prefectureKey } }));
          dispatch(loadRelated({ jobTypeId, prefectureId: prefectureKey, requestableOnly: true }));
          dispatch(listPopular({ prefectureKey }));
          dispatch(listCities({ cityKey, jobTypeId, prefectureKey }));
          dispatch(getNewestReviewsByJobTypeAndPrefecture(jobTypeId, prefectureKey ?? ''));
          break;
        case CITY_PAGE:
        case GROUP_CITY_PAGE:
          dispatch(setNewRequestModalContext({ city: { key: cityKey } }));
          dispatch(listPrefectures({ jobTypeId, prefectureKey }));
          dispatch(listCities({ cityKey, jobTypeId, prefectureKey }));
          dispatch(listPopular({ cityKey, prefectureKey }));
          dispatch(getNewestReviewsByJobTypeAndPrefecture(jobTypeId, prefectureKey ?? ''));
          break;
      }
      dispatch(
        getPopularPricingItems({ cityKey, jobTypeId, language: currentLocale, prefectureKey })
      );

      await logicMiddleware.whenComplete();
      const proCountCity = proCountSelector(store.getState());
      const proCountGroup = proCountGroupSelector(store.getState());
      const totalPros = Number(proCountCity) + Number(proCountGroup);
      const totalCaseStudies = caseStudiesTotalCountSelector(store.getState());
      const caseStudyListSize = caseStudiesPageSizeSelector(store.getState());

      const isReviewsQAEnabled = await featureFlagsService.isReviewsQAEnabled();

      // If the specified pro page number is fatigued with Max value, 404 shall be set.
      if (
        totalPros !== 0 &&
        initialPageNumber > Math.ceil(Number(proCountCity) / GROUP_CITY_NUM_CITY_PROS_PER_PAGE)
      ) {
        return { redirect: { destination: '/404', permanent: false } };
      }
      // If the specified caseStudy page number is fatigued with Max value, 404 shall be set.
      if (
        totalCaseStudies !== 0 &&
        caseStudyListSize !== 0 &&
        initialPageNumberForCaseStudy > Math.ceil(totalCaseStudies / caseStudyListSize)
      ) {
        return { redirect: { destination: '/404', permanent: false } };
      }

      const featureFlags = { isReviewsQAEnabled };
      if (totalPros === 0) {
        const path = [
          category1,
          category2,
          category3,
          jobTypeId,
          prefectureKey,
          pageType === GROUP_PAGE ? groupKey : cityKey,
        ].filter(isDefined);

        if (priceRange || matchingCriteria || day || time) {
          const redirectURL = `${language === 'en' ? '/en/' : '/'}${path.join('/')}`;

          return { redirect: { destination: redirectURL, permanent: false } };
        }

        const params = new URLSearchParams({ ...restQueryString }).toString();

        path.pop();
        const redirectURL = `${language === 'en' ? '/en/' : '/'}${path.join('/')}${
          params ? `?${params}` : ''
        }`;
        return { redirect: { destination: redirectURL, permanent: false } };
      }

      const serverSideProps = removeUndefinedForNextJsSerializing({
        ...i18nData,
        cityGroup: groupKey,
        cityGroupName,
        numOfCityPros,
        numOfGroupPros,
        totalPros,
        featureFlags,
        groupLink,
        cityId: cityKey,
        day,
        initialPageNumber,
        initialPageNumberForCaseStudy,
        internalBlogPosts: internalBlogPostItems,
        jobType,
        jobTypeId,
        online: Boolean(online),
        pageTranslationId,
        pageType,
        prefectureId: prefectureKey,
        shouldOpenRequestModal: Boolean(shouldOpenRequestModal),
        time,
      });

      setSSRCache(cacheKeyProps, serverSideProps, 'props');
      setSSRCache(cacheKeyProps, store.getState(), 'states');

      return { props: serverSideProps };
    }
);

export default VerticalPage;
